import React, { useRef, useState } from 'react';
import { Field, Formik, Form, FieldArray } from 'formik';
import { useStore } from 'react-redux';
import { keyBy, mapValues, uniq } from 'lodash';
import PropTypes from 'prop-types';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { MessageBar, MessageBarType, DetailsRow, SelectionMode } from 'office-ui-fabric-react';
import moment from 'moment';
import { recoupmentPercentColumn } from '../DynamicDetailsList/recoupmentColumns';
import styles from './GroupAssignmentsForm.style';
import DynamicDetailsList from '../DynamicDetailsList/DynamicDetailsList';
import MultiPickerFormik from '../MultiPicker/MultiPickerFormik';
import TextButton from '../TextButton';
import { pivotWider, rowTransformer } from '../DynamicDetailsList/RowTransformation';
import {
  defaultTransformer,
  defaultPivotWiderTransformer,
  allColumns,
} from '../DynamicDetailsList/ContractTransactionColumns';
import theme from '../../theme.style';
import { pxToRem } from '../../utils';
import { effectiveDateColumn, expirationDateColumn } from '../DynamicDetailsList/DateColumns';
import { menuColumn } from '../DynamicDetailsList/generalColumns';

const defaultMinDate = moment.utc().startOf('day').toDate();
const defaultMaxDate = moment.utc('2099-12-31').startOf('day').toDate();

/**
 * Convert a groupAssignment to formik values which needed to render
 * the "Group Assignment" rows.
 *
 * @param {Object} filter
 * @param {Object} store
 * @returns
 */
const filtersToFormikValue = (filter, store) => {
  const formikGroupAssignmentFilter = { ...filter };

  // update calcParams
  filter.recoupmentParams?.forEach(recoupParam => {
    formikGroupAssignmentFilter[recoupParam.property] = recoupParam.value;
  });

  // convert dimensions to formik values
  const dimensionsToFormikValues = pivotWider(
    mapValues(keyBy(allColumns, 'key'), 'pivotWiderTransformer'),
    defaultPivotWiderTransformer,
    store,
  );

  const formikDimensionValues = dimensionsToFormikValues(filter);

  Object.assign(formikGroupAssignmentFilter, formikDimensionValues);

  return formikGroupAssignmentFilter;
};

/**
 * Convert an agreement from the API to the formik values needed to render
 * the agreement values.
 *
 * @param {Object} groupAssignments
 * @param {Object} store
 * @returns
 */
const groupAssignmentsToFormikValues = (groupAssignments, store) => {
  const initialValues = {
    dimensions: [],
    filters: [
      {
        type: 'include',
        effectiveDate: defaultMinDate,
        terminationDate: defaultMaxDate,
        recoupmentParams: [{ property: 'recoupmentPercentage', value: 100 }],
      },
    ],
  };

  if (!groupAssignments) {
    return initialValues;
  }

  if (groupAssignments.dimensions) {
    initialValues.dimensions = allColumns.filter(column =>
      groupAssignments.dimensions.includes(column.key),
    );
  }

  if (groupAssignments.filters) {
    initialValues.filters = groupAssignments.filters.map(groupAssignmentFilter =>
      filtersToFormikValue(groupAssignmentFilter, store),
    );
  }

  return initialValues;
};

const shouldNotAllowMultipleAssignments = (
  values,
  contractEffectiveDate,
  contractTerminationDate,
) => {
  const filterTransformer = rowTransformer(
    { ...values.dimensions },
    defaultTransformer,
    'effectiveDate',
    'terminationDate',
    'type',
    'recoupmentParams',
  );
  const filters = values.filters.map(f => ({
    ...f,
    dimensions: filterTransformer(f).dimensions,
  }));

  const isOnefilter = filters.length === 1;
  const dimensionsAreAll = !filters[0]?.dimensions || filters[0].dimensions.length === 0;
  const datesAreSameAsContractDates =
    moment.utc(filters[0]?.effectiveDate).isSame(contractEffectiveDate) &&
    moment.utc(filters[0]?.terminationDate).isSameOrAfter(contractTerminationDate);

  if (isOnefilter && dimensionsAreAll && datesAreSameAsContractDates) return true;

  return false;
};

const GroupAssignmentsForm = ({
  advanceGroup,
  onSubmit,
  onCancel,
  setFieldValue,
  setFieldTouched,
}) => {
  const store = useStore();
  const initialValues = groupAssignmentsToFormikValues(advanceGroup.groupAssignments, store);
  const formRef = useRef();
  const [isAllowedMoreFilters, setIsAllowedMoreFilters] = useState(true);

  const createNewGroupAssignment = (advancegroup, values, arrayHelpers) => {
    const contractEffectiveDate = advancegroup?.contract?.effectiveDate;
    const contractTerminationDate = advancegroup?.contract?.terminationDate;

    if (
      values.filters &&
      shouldNotAllowMultipleAssignments(values, contractEffectiveDate, contractTerminationDate)
    ) {
      setIsAllowedMoreFilters(false);
      return;
    }

    setIsAllowedMoreFilters(true);
    arrayHelpers.push({
      _id: 0,
      effectiveDate: contractEffectiveDate || defaultMinDate,
      terminationDate: contractTerminationDate || defaultMaxDate,
      type: 'include',
      recoupmentPercentage: 0,
    });
  };

  const dateColumns = [effectiveDateColumn(), expirationDateColumn(defaultMinDate, defaultMaxDate)];

  const rowValidator = row => {
    const errors = { rowErrors: [] };

    const invalidRecoupmentRangeMessage =
      'Recoupment percentage cannot be less than or equal to 0% or greater than 100%';

    if (!row.recoupmentPercentage) {
      if (row.recoupmentPercentage === 0) {
        errors.recoupmentPercentage = 'Invalid Range';
        errors.rowErrors.push(invalidRecoupmentRangeMessage);
      } else {
        errors.recoupmentPercentage = 'Invalid Value';
        errors.rowErrors.push('Recoupment percentage contains invalid value');
      }
    }

    if (
      row.recoupmentPercentage &&
      (row.recoupmentPercentage < 0 || row.recoupmentPercentage > 100)
    ) {
      errors.recoupmentPercentage = 'Invalid Range';
      errors.rowErrors.push(invalidRecoupmentRangeMessage);
    }

    if (
      row.effectiveDate &&
      row.terminationDate &&
      moment(row.effectiveDate).isAfter(row.terminationDate)
    ) {
      errors.effectiveDate = 'Invalid Range';
      errors.terminationDate = 'Invalid Range';
      errors.rowErrors.push('Expiration Date must be after effective date.');
    }

    if (errors.rowErrors.length === 0) return undefined;
    return errors;
  };

  const formValidator = values => {
    if (values.filters.length === 0) {
      const filterErrors = ['At least one filter must be assigned.'];
      return { filters: [], filterErrors };
    }

    const filterTransformer = rowTransformer(
      { ...values.dimensions },
      defaultTransformer,
      'effectiveDate',
      'terminationDate',
      'type',
      'recoupmentParams',
    );
    const filtersValidation = values.filters
      .map(f => ({ ...f, dimensions: filterTransformer(f).dimensions }))
      .map(row => rowValidator(row));

    const filterErrors = filtersValidation
      ?.map(e => e?.rowErrors)
      .reduce((a, b) => a.concat(b), [])
      .filter(err => err !== undefined);

    if (filterErrors.every(e => !e)) return {};
    return { filters: filtersValidation, filterErrors };
  };

  const submitHandler = async values => {
    const { dimensions } = values;

    const mapRecoupmentParams = filter => {
      const recoupParamKeys = ['recoupmentPercentage'];
      const recoupParams = [];
      recoupParamKeys.forEach(param => {
        if (filter[param] || filter[param] === 0) {
          recoupParams.push({ property: param, value: filter[param] });
        }
      });
      return recoupParams;
    };

    const groupAssignmentFilters = values.filters.map(filter => ({
      ...filter,
      type: 'include',
      effectiveDate: moment.utc(filter.effectiveDate).format('YYYY-MM-DD'),
      terminationDate: moment.utc(filter.terminationDate).format('YYYY-MM-DD'),
      recoupmentParams: mapRecoupmentParams(filter),
    }));

    return onSubmit(
      { dimensions, filters: groupAssignmentFilters },
      setFieldValue,
      setFieldTouched,
    );
  };

  return (
    <Formik
      validate={formValidator}
      innerRef={formRef}
      initialValues={initialValues}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={submitHandler}
    >
      {({ values, submitForm, errors }) => {
        const onRenderRow = props => {
          const customStyle = {
            root: {
              backgroundColor: theme.palette.neutralWhite,
              selectors: {
                '& .ms-DetailsRow-fields .ms-DetailsRow-cell': {
                  padding: `${pxToRem(8)} ${pxToRem(4)}`,
                },
                '& .ms-Dropdown-container > .ms-Stack': {
                  marginBottom: `${pxToRem(0)} !important`,
                },
              },
            },
          };
          // eslint-disable-next-line react/prop-types
          if (errors.filters && errors.filters[props.itemIndex])
            customStyle.root = {
              backgroundColor: theme.palette.accentErrorOpaque,
              ':active': { backgroundColor: theme.palette.accentErrorOpaque },
              ':hover': { backgroundColor: theme.palette.accentErrorOpaque },
            };
          return <DetailsRow {...props} styles={customStyle} />;
        };
        const uniqueErrors = uniq(errors.filterErrors);

        return (
          <div>
            {!isAllowedMoreFilters && (
              <MessageBar
                className={styles.errorBar}
                isMultiline
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
              >
                Cannot add more filters if no specific dimensions are selected, and the effective
                and expiration dates are the same as contract effective date{' '}
                {moment.utc(advanceGroup?.contract?.effectiveDate).format('YYYY-MM-DD')} and
                expiration date{' '}
                {moment.utc(advanceGroup?.contract?.terminationDate).format('YYYY-MM-DD')}.
              </MessageBar>
            )}
            {errors.filterErrors && errors.filterErrors.length > 0 && (
              <MessageBar
                className={styles.errorBar}
                isMultiline
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
              >
                Cannot save filters:
                {uniqueErrors.map(error => {
                  return <li key={error}>{error}</li>;
                })}
              </MessageBar>
            )}
            <Form className={styles.royaltyFeeForm} data-testid="groupAssignmentForm">
              <div className={styles.assignmentDimensions}>
                <div className={styles.dimensionsContainer}>
                  <Field
                    label="Assignment Dimensions"
                    name="dimensions"
                    multiSelect
                    component={MultiPickerFormik}
                    options={allColumns}
                    className={styles.dimensionsSelector}
                    multiTag
                    data-testid="assigned-dimensions"
                  />
                </div>

                <FieldArray name="filters">
                  {arrayHelpers => {
                    const columns = [
                      recoupmentPercentColumn(),
                      ...values.dimensions,
                      ...dateColumns,
                      menuColumn(),
                    ];

                    return (
                      <>
                        {values.filters.length > 0 && (
                          <Field
                            name="filters"
                            component={DynamicDetailsList}
                            columns={columns}
                            arrayHelpers={arrayHelpers}
                            selectionMode={SelectionMode.none}
                            enableRowReorder
                            onRenderRow={onRenderRow}
                          />
                        )}
                        <div className={styles.buttonContainer}>
                          <TextButton
                            type="button"
                            data-testid="add-assignment-link"
                            onClick={() => {
                              createNewGroupAssignment(advanceGroup, values, arrayHelpers);
                            }}
                          >
                            Add Assignment
                          </TextButton>
                        </div>
                      </>
                    );
                  }}
                </FieldArray>
              </div>

              <div className={styles.formActions}>
                <DefaultButton
                  className={styles.cancelButton}
                  onClick={onCancel}
                  data-testid="cancel-button"
                >
                  Cancel
                </DefaultButton>
                <PrimaryButton onClick={submitForm} data-testid="save-groupAssignment-button">
                  Save
                </PrimaryButton>
              </div>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

GroupAssignmentsForm.propTypes = {
  advanceGroup: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
};

GroupAssignmentsForm.defaultProps = {
  advanceGroup: null,
};

export default GroupAssignmentsForm;
